/**
 * Evidence Modal — Clinical Studies
 * Zellaforte Brand Design System
 *
 * Opens a right-side drawer (desktop) or bottom sheet (mobile)
 * displaying clinical evidence data for a complex.
 *
 * v3 — Swiper-based KPI carousel (matches homepage tabs-with-img-section
 * pattern for smoothness + .swiper-pagination-bullet pill+dots indicator).
 */
const EVIDENCE_MODAL_BUILD = 'v3-swiper-kpi';

(function() {
  'use strict';

  // DOM references
  let overlay = null;
  let modal = null;
  let closeBtn = null;
  let lastFocusedElement = null;

  // Data attribute selectors
  const TRIGGER_SELECTOR = '[data-evidence-trigger]';
  const OVERLAY_SELECTOR = '[data-evidence-modal-overlay]';
  const MODAL_SELECTOR = '[data-evidence-modal-panel]';
  const CLOSE_SELECTOR = '[data-evidence-modal-close]';

  // Content slot selectors
  const SLOTS = {
    title: '[data-evidence-title]',
    subtitle: '[data-evidence-subtitle]',
    statsGrid: '[data-evidence-stats-grid]',
    statsContext: '[data-evidence-stats-context]',
    whatIsIt: '[data-evidence-what-is-it]',
    findings: '[data-evidence-findings]',
    details: '[data-evidence-details]',
    citations: '[data-evidence-citations]',
    strains: '[data-evidence-strains]',
    chips: '[data-evidence-chips]'
  };

  /**
   * Initialize the modal system
   */
  function init() {
    overlay = document.querySelector(OVERLAY_SELECTOR);

    // Portal to <body> to escape .shopify-section stacking context
    if (overlay) document.body.appendChild(overlay);

    modal = document.querySelector(MODAL_SELECTOR);
    closeBtn = document.querySelector(CLOSE_SELECTOR);

    if (!overlay || !modal) {
      console.warn('Evidence modal: Required elements not found');
      return;
    }

    // Bind trigger clicks
    document.addEventListener('click', handleTriggerClick);

    // Bind close actions
    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal);
    }
    overlay.addEventListener('click', handleOverlayClick);
    document.addEventListener('keydown', handleKeyDown);
  }

  /**
   * Handle clicks on evidence triggers
   */
  function handleTriggerClick(e) {
    const trigger = e.target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;

    e.preventDefault();
    lastFocusedElement = trigger;

    // Get data from trigger
    const data = getDataFromTrigger(trigger);
    populateModal(data);
    openModal();
  }

  /**
   * Extract data from trigger element's data attributes
   */
  function getDataFromTrigger(trigger) {
    const data = {
      title: trigger.dataset.evidenceTitle || '',
      subtitle: trigger.dataset.evidenceSubtitle || '',
      whatIsIt: trigger.dataset.evidenceWhatIsIt || '',
      statsContext: trigger.dataset.evidenceStatsContext || '',
      studiesSummary: trigger.dataset.evidenceStudiesSummary || '',
      citationsLine: trigger.dataset.evidenceCitations || '',
      strainList: trigger.dataset.evidenceStrains || '',
      chips: [],
      stats: [],
      categories: [],
      findings: [],
      details: []
    };

    // Parse chips (chip_1, chip_2, chip_3)
    for (let i = 1; i <= 5; i++) {
      const chip = trigger.dataset[`evidenceChip${i}`];
      if (chip) data.chips.push(chip);
    }

    // Parse categories (for tabbed display like Kera-Diet)
    // Format: data-evidence-category-1-name, data-evidence-category-1-stats (JSON array)
    for (let i = 1; i <= 3; i++) {
      const name = trigger.dataset[`evidenceCategory${i}Name`];
      const statsJson = trigger.dataset[`evidenceCategory${i}Stats`];
      if (name && statsJson) {
        try {
          const stats = JSON.parse(statsJson);
          data.categories.push({ name, stats });
        } catch (e) {
          console.warn('Failed to parse category stats JSON:', e);
        }
      }
    }

    // Parse stats (stat_1_value, stat_1_label, etc.) - fallback if no categories
    for (let i = 1; i <= 12; i++) {
      const value = trigger.dataset[`evidenceStat${i}Value`];
      const label = trigger.dataset[`evidenceStat${i}Label`];
      if (value && label) {
        data.stats.push({ value, label });
      }
    }

    // Parse evidence findings (evidence_1, evidence_2, evidence_3)
    for (let i = 1; i <= 5; i++) {
      const finding = trigger.dataset[`evidenceFinding${i}`];
      if (finding) data.findings.push(finding);
    }

    // Parse study details
    const isDE = document.documentElement.lang === 'de' || window.location.pathname.startsWith('/de');
    const detailKeys = [
      { key: 'trialDesign', label: isDE ? 'Studiendesign' : 'Design' },
      { key: 'duration', label: isDE ? 'Dauer' : 'Duration' },
      { key: 'population', label: isDE ? 'Teilnehmer' : 'Population' },
      { key: 'doseUsed', label: isDE ? 'Dosierung' : 'Dose' }
    ];
    detailKeys.forEach(({ key, label }) => {
      const value = trigger.dataset[`evidence${key.charAt(0).toUpperCase() + key.slice(1)}`];
      if (value) data.details.push({ key: label, value });
    });

    return data;
  }

  // Store current data for tab switching
  let currentData = null;

  // Swiper instance for the KPI carousel — destroyed and recreated on
  // each category-tab change and on modal close.
  let kpiSwiper = null;

  /**
   * Destroy the active KPI Swiper instance, if any. Safe to call when none
   * exists. Called before re-render and on modal close.
   */
  function destroyKpiSwiper() {
    if (kpiSwiper && typeof kpiSwiper.destroy === 'function') {
      try {
        kpiSwiper.destroy(true, true);
      } catch (e) {
        // ignore — Swiper may have been garbage-collected if the DOM changed
      }
    }
    kpiSwiper = null;
  }

  /**
   * Populate modal with data
   */
  function populateModal(data) {
    currentData = data;
    
    // Title
    const titleEl = modal.querySelector(SLOTS.title);
    if (titleEl) titleEl.textContent = data.title;

    // Subtitle
    const subtitleEl = modal.querySelector(SLOTS.subtitle);
    if (subtitleEl) {
      subtitleEl.textContent = data.subtitle;
      subtitleEl.style.display = data.subtitle ? '' : 'none';
    }

    // Stats Section - check if we have categories (tabs) or simple stats
    const statsSection = modal.querySelector('.evidence-stats-section');
    const statsGridEl = modal.querySelector(SLOTS.statsGrid);
    
    if (data.categories && data.categories.length > 1) {
      // Render with category tabs (2+ categories)
      renderCategoryTabs(data, statsSection, statsGridEl);
    } else if (data.categories && data.categories.length === 1) {
      // Single category - show as header without tabs
      statsSection.classList.remove('has-tabs');
      removeTabsIfPresent(statsSection);
      const cat = data.categories[0];
      statsGridEl.innerHTML = `
        <div class="evidence-category-header">${escapeHtml(cat.name)}</div>
        ${cat.stats.map(stat => `
          <div class="evidence-stat-card">
            <div class="evidence-stat-value">${escapeHtml(stat.value)}</div>
            <div class="evidence-stat-label">${escapeHtml(stat.label)}</div>
          </div>
        `).join('')}
      `;
      statsSection.style.display = '';
    } else if (statsGridEl && data.stats.length > 0) {
      // Simple stats without tabs
      statsSection.classList.remove('has-tabs');
      removeTabsIfPresent(statsSection);
      statsGridEl.innerHTML = data.stats.map(stat => `
        <div class="evidence-stat-card">
          <div class="evidence-stat-value">${escapeHtml(stat.value)}</div>
          <div class="evidence-stat-label">${escapeHtml(stat.label)}</div>
        </div>
      `).join('');
      statsSection.style.display = '';
    } else if (statsSection) {
      statsSection.style.display = 'none';
    }

    // Stats Context (simple context line)
    const statsContextEl = modal.querySelector(SLOTS.statsContext);
    if (statsContextEl) {
      statsContextEl.textContent = data.statsContext;
      statsContextEl.style.display = data.statsContext ? '' : 'none';
    }
    
    // Studies Summary (more detailed, for categorized ingredients)
    renderStudiesSummary(data, statsSection);

    // What is it
    const whatIsItEl = modal.querySelector(SLOTS.whatIsIt);
    if (whatIsItEl) {
      if (data.whatIsIt) {
        whatIsItEl.querySelector('.evidence-section-body').textContent = data.whatIsIt;
        whatIsItEl.style.display = '';
      } else {
        whatIsItEl.style.display = 'none';
      }
    }

    // Chips
    const chipsEl = modal.querySelector(SLOTS.chips);
    if (chipsEl) {
      if (data.chips.length > 0) {
        chipsEl.innerHTML = data.chips.map(chip => 
          `<span class="evidence-chip">${escapeHtml(chip)}</span>`
        ).join('');
        chipsEl.style.display = '';
      } else {
        chipsEl.style.display = 'none';
      }
    }

    // Findings
    const findingsEl = modal.querySelector(SLOTS.findings);
    if (findingsEl) {
      if (data.findings.length > 0) {
        findingsEl.querySelector('.evidence-findings').innerHTML = data.findings.map(f => 
          `<li>${escapeHtml(f)}</li>`
        ).join('');
        findingsEl.style.display = '';
      } else {
        findingsEl.style.display = 'none';
      }
    }

    // Details
    const detailsEl = modal.querySelector(SLOTS.details);
    if (detailsEl) {
      if (data.details.length > 0) {
        detailsEl.querySelector('.evidence-details-grid').innerHTML = data.details.map(d => `
          <div class="evidence-detail-row">
            <span class="evidence-detail-key">${escapeHtml(d.key)}</span>
            <span class="evidence-detail-value">${escapeHtml(d.value)}</span>
          </div>
        `).join('');
        detailsEl.style.display = '';
      } else {
        detailsEl.style.display = 'none';
      }
    }

    // Citations
    const citationsEl = modal.querySelector(SLOTS.citations);
    if (citationsEl) {
      if (data.citationsLine) {
        citationsEl.querySelector('.evidence-citations-text').textContent = data.citationsLine;
        citationsEl.style.display = '';
      } else {
        citationsEl.style.display = 'none';
      }
    }

    // Strain list
    const strainsEl = modal.querySelector(SLOTS.strains);
    if (strainsEl) {
      if (data.strainList) {
        const strains = data.strainList.split('\n').filter(s => s.trim());
        strainsEl.querySelector('.evidence-strains-list').innerHTML = strains.map(s => 
          `<li>${escapeHtml(s.trim())}</li>`
        ).join('');
        strainsEl.style.display = '';
      } else {
        strainsEl.style.display = 'none';
      }
    }
  }

  /**
   * Open modal
   *
   * iOS body scroll lock: overflow:hidden alone does NOT prevent
   * document touch-scroll on iOS Safari. We must also set
   * position:fixed with the saved scroll offset so the document
   * is genuinely frozen. Without this, swiping inside the modal
   * scrolls the document behind, making the modal feel unresponsive.
   */
  function openModal() {
    overlay.setAttribute('aria-hidden', 'false');

    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    document.body.dataset.evidenceScrollY = String(scrollY);
    document.body.classList.add('evidence-modal-open');
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    // Focus trap - focus close button
    setTimeout(() => {
      if (closeBtn) closeBtn.focus();
    }, 100);
  }

  /**
   * Close modal
   */
  function closeModal() {
    overlay.setAttribute('aria-hidden', 'true');

    // Free the KPI Swiper so its observers don't leak between opens.
    destroyKpiSwiper();

    const scrollY = parseInt(document.body.dataset.evidenceScrollY || '0', 10);
    document.body.classList.remove('evidence-modal-open');
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    delete document.body.dataset.evidenceScrollY;
    window.scrollTo(0, scrollY);

    // Return focus
    if (lastFocusedElement) {
      lastFocusedElement.focus();
      lastFocusedElement = null;
    }
  }

  /**
   * Handle overlay click (close on backdrop)
   */
  function handleOverlayClick(e) {
    if (e.target === overlay) {
      closeModal();
    }
  }

  /**
   * Handle keyboard events
   */
  function handleKeyDown(e) {
    if (overlay.getAttribute('aria-hidden') === 'false') {
      if (e.key === 'Escape') {
        closeModal();
      }
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Render category tabs for multi-category ingredients (like Kera-Diet)
   */
  function renderCategoryTabs(data, statsSection, statsGridEl) {
    statsSection.classList.add('has-tabs');
    statsSection.style.display = '';
    
    // Remove existing tabs if present
    removeTabsIfPresent(statsSection);
    
    // Create tabs container
    const tabsContainer = document.createElement('div');
    tabsContainer.className = 'evidence-category-tabs';
    tabsContainer.innerHTML = data.categories.map((cat, idx) => `
      <button type="button" class="evidence-category-tab${idx === 0 ? ' is-active' : ''}" 
              data-category-index="${idx}">
        ${escapeHtml(cat.name)}
      </button>
    `).join('');
    
    // Insert tabs before the stats grid
    statsGridEl.parentNode.insertBefore(tabsContainer, statsGridEl);
    
    // Render first category stats
    renderCategoryStats(data.categories[0].stats, statsGridEl);
    
    // Bind tab click events
    tabsContainer.querySelectorAll('.evidence-category-tab').forEach(tab => {
      tab.addEventListener('click', (e) => {
        const index = parseInt(e.target.dataset.categoryIndex, 10);
        
        // Update active state
        tabsContainer.querySelectorAll('.evidence-category-tab').forEach(t => 
          t.classList.remove('is-active')
        );
        e.target.classList.add('is-active');
        
        // Render new category stats
        if (currentData && currentData.categories[index]) {
          renderCategoryStats(currentData.categories[index].stats, statsGridEl);
        }
      });
    });
  }

  /**
   * Render stats for a specific category.
   *
   * Layout strategy:
   *  - 1–3 stats: simple flex row (no horizontal carousel needed). The
   *    existing CSS grid auto-fit on .evidence-stats-grid handles spacing.
   *  - 4+ stats: render as a Swiper carousel with pagination, mirroring
   *    the homepage `tabs-with-img-section` pattern. Smooth touch scroll
   *    and the elegant `.swiper-pagination-bullet` pill+dots indicator
   *    come from the global Swiper bundle (loaded in layout/theme.liquid)
   *    and base.css :3810–3842.
   */
  function renderCategoryStats(stats, statsGridEl) {
    // Tear down any previous Swiper before re-rendering — innerHTML
    // assignment below would orphan the instance.
    destroyKpiSwiper();

    if (!stats || stats.length === 0) {
      statsGridEl.innerHTML = '';
      return;
    }

    const cardsHtml = stats.map(stat => `
      <div class="evidence-stat-card swiper-slide">
        <div class="evidence-stat-value">${escapeHtml(stat.value)}</div>
        <div class="evidence-stat-label">${escapeHtml(stat.label)}</div>
      </div>
    `).join('');

    if (stats.length <= 3) {
      // Few stats — keep the grid layout, no Swiper needed.
      statsGridEl.innerHTML = stats.map(stat => `
        <div class="evidence-stat-card">
          <div class="evidence-stat-value">${escapeHtml(stat.value)}</div>
          <div class="evidence-stat-label">${escapeHtml(stat.label)}</div>
        </div>
      `).join('');
      return;
    }

    // 4+ stats — Swiper carousel.
    statsGridEl.innerHTML = `
      <div class="swiper evidence-stats-swiper">
        <div class="swiper-wrapper">${cardsHtml}</div>
        <div class="swiper-pagination"></div>
      </div>
    `;

    const swiperEl = statsGridEl.querySelector('.evidence-stats-swiper');
    if (!swiperEl || typeof window.Swiper !== 'function') return;

    kpiSwiper = new window.Swiper(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 12,
      grabCursor: true,
      pagination: {
        el: swiperEl.querySelector('.swiper-pagination'),
        clickable: true,
      },
    });
  }

  /**
   * Remove tabs if present (for cleanup)
   */
  function removeTabsIfPresent(statsSection) {
    const existingTabs = statsSection.querySelector('.evidence-category-tabs');
    if (existingTabs) existingTabs.remove();

    // Tear down any active Swiper on the KPI grid before its inner
    // markup is replaced.
    destroyKpiSwiper();

    const existingSummary = statsSection.querySelector('.evidence-studies-summary');
    if (existingSummary) existingSummary.remove();
  }

  /**
   * Render studies summary line
   */
  function renderStudiesSummary(data, statsSection) {
    // Remove existing if present
    const existing = statsSection.querySelector('.evidence-studies-summary');
    if (existing) existing.remove();
    
    if (!data.studiesSummary) return;
    
    const summaryEl = document.createElement('div');
    summaryEl.className = 'evidence-studies-summary';
    summaryEl.innerHTML = data.studiesSummary;
    
    statsSection.appendChild(summaryEl);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
